import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { onAnimationEnd, isClickedOutside } from '@theme/utilities';

/**
 * A custom element that manages a hotspot modal with positioning relative to the trigger button.
 *
 * @extends DialogComponent
 */
export class HotspotModalComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.#setupPositioning();
    this.currentTriggerButton = "";
  }

  /**
   * Shows the dialog and positions it relative to the trigger button.
   */
  showDialog() {
    const triggerButton = document.querySelector(`[on\\:click*="${this.id}/showDialog"]`);
    this.currentTriggerButton = triggerButton;
    
    if (triggerButton) {
      this.#positionModal(triggerButton);
    }

    const { dialog } = this.refs;

    if (dialog.open) return;

    // document.body.style.width = '100%';
    // const scrollY = window.scrollY;
    // document.body.style.position = 'fixed';
    // document.body.style.top = `-${scrollY}px`;

    // dialog.showModal();
    // this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    document.body.classList.add("show__modal--hotpot");
    setTimeout(() => {
      document.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
    // window.addEventListener("scroll", () => {
    //   this.handleScroll(this.currentTriggerButton);
    // });
    // window.addEventListener("resize", this.handleScroll);
  }

  handleScroll() {
    if (!this.currentTriggerButton) return;

    if (this.rafId) cancelAnimationFrame(this.rafId);

    this.rafId = requestAnimationFrame(() => {
      this.#positionModal(this.currentTriggerButton);
    });
  }

  /**
   * Positions the modal relative to the trigger button
   * @param {Element} triggerButton - The button that triggered the modal
   */
  #positionModal(triggerButton) {
    const { dialog } = this.refs;
    if (!dialog) return;

    // Reset arrow and content positioning first
    this.#resetArrowPositioning(dialog);

    // Get trigger button position
    const buttonRect = triggerButton.getBoundingClientRect();
    const buttonCenterX = buttonRect.left + buttonRect.width / 2;
    const buttonCenterY = buttonRect.top + buttonRect.height / 2;
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Check if this is an image-only modal
    const isImageOnly = this.#isImageOnlyModal(dialog);
    
    // Get actual modal dimensions after it's rendered
    const modalRect = dialog.getBoundingClientRect();
    let modalWidth = modalRect.width || parseInt(getComputedStyle(dialog).getPropertyValue('--modal-width')) || 320;
    let modalHeight = modalRect.height || 150;
    
    // Adjust dimensions for image-only modals
    if (isImageOnly) {
      modalWidth = 235; // Fixed width for image-only modals
      modalHeight = 230; // Fixed height for image-only modals
    }
    
    // Calculate optimal position
    let left = buttonCenterX - (modalWidth / 2);
    let top = buttonRect.bottom + 20; // 20px below the button
    let isAbove = false;
    
    // Adjust for viewport boundaries
    const padding = 16;
    
    // Horizontal adjustments
    if (left < padding) {
      left = padding;
    } else if (left + modalWidth > viewportWidth - padding) {
      left = viewportWidth - modalWidth - padding;
    }
    
    // Vertical adjustments - if modal would go below viewport, show above button
    // if (top + modalHeight > viewportHeight - padding) {
    //   // Add extra spacing for image-only modals to prevent overlap with hotspot button
    //   const extraSpacing = isImageOnly ? 60 : 20; // More space for image-only modals
    //   top = buttonRect.top - modalHeight - extraSpacing;
    //   isAbove = true;
    // }
    
    // Ensure modal doesn't go above viewport
    // if (top < padding) {
    //   top = padding;
    // }
    
    // Apply positioning
    dialog.style.position = 'fixed';
    dialog.style.left = `${left}px`;
    dialog.style.top = `${top}px`;
    dialog.style.margin = '0';
    dialog.style.transform = 'none';
    
    const _btn = triggerButton.closest(".image-hotspots")?.querySelector(".hotspot-modal.active");
    if (_btn) {
      _btn?.classList.remove("active");
    }
    dialog.classList.add("active");
    
    // Position and orient the arrow to point to the button center
    this.#positionArrow(dialog, buttonCenterX - left, modalWidth);
    if (isAbove) {
      this.#flipArrow(dialog);
    }
  }

  /**
   * Checks if the modal is image-only type
   * @param {Element} dialog - The dialog element
   * @returns {boolean} True if the modal is image-only
   */
  #isImageOnlyModal(dialog) {
    const imageOnlyContainer = dialog.querySelector('.hotspot-modal__image-only');
    return imageOnlyContainer !== null;
  }

  /**
   * Positions the arrow to point to the button
   * @param {Element} dialog - The dialog element
   * @param {number} arrowOffset - Horizontal offset for the arrow from the left edge of the modal
   * @param {number} modalWidth - Width of the modal
   */
  #positionArrow(dialog, arrowOffset, modalWidth) {
    const arrow = /** @type {HTMLElement} */ (dialog.querySelector('.hotspot-modal__arrow'));
    if (!arrow) return;
    
    // Calculate arrow position relative to the modal center
    const arrowWidth = 24;
    const modalCenter = modalWidth / 2;
    const arrowCenter = arrowWidth / 2;
    
    // Calculate the offset from the modal center to position the arrow center under the button
    const offsetFromCenter = arrowOffset - modalCenter;
    
    // Clamp arrow position to stay within reasonable bounds (with some padding)
    const padding = arrowCenter + 4; // 4px padding from edges
    const minOffset = -modalCenter + padding;
    const maxOffset = modalCenter - padding;
    
    const clampedOffset = Math.max(minOffset, Math.min(offsetFromCenter, maxOffset));
    
    // Position the arrow relative to its container
    arrow.style.position = 'relative';
    arrow.style.left = `${clampedOffset}px`;
  }

  /**
   * Flips the arrow to point upward when modal is above the button
   * @param {Element} dialog - The dialog element
   */
  #flipArrow(dialog) {
    const arrow = /** @type {HTMLElement} */ (dialog.querySelector('.hotspot-modal__arrow'));
    if (!arrow) return;
    
    arrow.style.transform = 'rotate(180deg)';
    
    // Move arrow to bottom of modal content
    const content = /** @type {HTMLElement} */ (dialog.querySelector('.hotspot-modal__content'));
    if (content) {
      content.style.flexDirection = 'column-reverse';
    }
  }

  /**
   * Resets arrow positioning to default state
   * @param {Element} dialog - The dialog element
   */
  #resetArrowPositioning(dialog) {
    const arrow = /** @type {HTMLElement} */ (dialog.querySelector('.hotspot-modal__arrow'));
    const content = /** @type {HTMLElement} */ (dialog.querySelector('.hotspot-modal__content'));
    
    if (arrow) {
      arrow.style.transform = '';
      arrow.style.position = '';
      arrow.style.left = '';
    }
    
    if (content) {
      content.style.flexDirection = '';
    }
  }

  /**
   * Sets up positioning on window resize
   */
  #setupPositioning() {
    /** @type {number | undefined} */
    let resizeTimeout;
    
    window.addEventListener('resize', () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (this.refs.dialog.open) {
          // Re-position if modal is open
          const triggerId = this.id.replace('hotspot-modal-', '');
          const triggerButton = document.querySelector(`[on\\:click*="${this.id}/showDialog"]`);
          
          if (triggerButton) {
            this.#positionModal(triggerButton);
          }
        }
      }, 100);
    });
  }

  /**
   * Override the close method to reset positioning
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    
    // Don't proceed if dialog is already closed
    if (!dialog) return;

    // Remove event listeners first
    document.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Reset positioning AFTER the animation completes to avoid visual flash
    this.#resetArrowPositioning(dialog);
    
    // Reset dialog positioning
    dialog.style.position = '';
    dialog.style.left = '';
    dialog.style.top = '';
    dialog.style.margin = '';
    dialog.style.transform = '';

    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    // window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });

    // dialog.close();
    dialog.classList.remove("active");
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
    setTimeout(() => {
      document.body.classList.remove("show__modal--hotpot");
      document.querySelector("header-component")?.setAttribute("data-sticky-state","idle");
    },300);
  };

  /**
   * Handle click events (click outside to close)
   * @param {MouseEvent} event - The mouse event
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;
    
    if (!dialog.contains(event.target)) {
      this.closeDialog();
    }
  };

  /**
   * Handle keyboard events (escape to close)
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };
}

if (!customElements.get('hotspot-modal-component')) {
  customElements.define('hotspot-modal-component', HotspotModalComponent);
}
